import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PieChart, Pie } from "recharts";
import {
  HiCheckCircle,
  HiXCircle,
  HiSquares2X2,
  HiListBullet,
  HiXMark,
} from "react-icons/hi2";
import {
  HiOutlineXCircle,
  HiOutlineMapPin,
  HiOutlineCheckCircle,
  HiOutlineChatBubbleLeft,
  HiOutlinePencilSquare,
  HiTrash,
} from "react-icons/hi2";

/* ----------------------- Utilities ---------------------- */
const formatHora = (hora24) => {
  if (!hora24) return "No definida";
  const [h, m] = hora24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hora12 = h % 12 === 0 ? 12 : h % 12;
  return `${hora12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

const tiempoRetraso = (horaLlegada, horaEsperada) => {
  if (!horaLlegada || !horaEsperada) return 0;
  const llegada = new Date(`1970-01-01T${horaLlegada}`);
  const esperada = new Date(`1970-01-01T${horaEsperada}`);
  return Math.round((llegada - esperada) / 60000);
};

const formatFecha = (fechaISO) => {
  if (!fechaISO) return "---";
  const fechaSinHora = fechaISO.split("T")[0];
  const [anio, mes, dia] = fechaSinHora.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${parseInt(dia, 10)} de ${meses[parseInt(mes, 10) - 1]}, ${anio}`;
};

const statusBadge = (status) => {
  const map = {
    Pendiente: "badge-yellow",
    Activo: "badge-green",
    Apartado: "badge-blue",
    Extendido: "badge-blue",
    Finalizado: "badge-gray",
    Rechazado: "badge-red",
    Cancelado: "badge-red",
  };
  return map[status] || "badge-gray";
};

const borderColorMap = {
  purple: "border-l-purple-600",
  orange: "border-l-amber-400",
  green: "border-l-emerald-500",
  red: "border-l-red-500",
  gray: "border-l-gray-400",
};

/* ================================================================== */
const OverviewChecadas = () => {
  const navigate = useNavigate();
  const [toggleActivo, setToggleActivo] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [filter, setFilter] = useState({ sede: "", status: "", tecnicos: [] });
  const [selectedTecnico, setSelectedTecnico] = useState({});
  const [cancelLoading, setCancelLoading] = useState(false);
  const [drawerReserva, setDrawerReserva] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const getColor = (tecnico) => {
    const reserva = tecnico.reserva;
    if (!tecnico.checado) return reserva ? "red" : "gray";
    if (!reserva?.entrada) return "green";
    const retraso = tiempoRetraso(tecnico.hora_llegada, reserva.entrada);
    if (retraso > 60) return "purple";
    if (retraso > 30) return "orange";
    return "green";
  };

  const handleOpenModal = async (tecnico) => {
    setOpenModal(true);
    setSelectedTecnico(tecnico);
    setDrawerReserva(null);
    if (tecnico.reserva?.id) {
      setDrawerLoading(true);
      try {
        const res = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${tecnico.reserva.id}`);
        setDrawerReserva(res.data);
      } catch (err) {
        console.error("Error al obtener detalle de reserva:", err);
      } finally {
        setDrawerLoading(false);
      }
    }
  };
  const handleCloseModal = () => { setOpenModal(false); setCancelLoading(false); setDrawerReserva(null); };

  /* -------------------- Borrar día sin asistencia -------------------- */
  const handleBorrarDiaItinerario = async (day) => {
    const tieneReporte = drawerReserva?.reportes_servicio?.some((r) => r.itinerario_id === day.id);
    if (tieneReporte) {
      alert("Este día tiene un reporte asociado y no puede eliminarse.");
      return;
    }
    if (!window.confirm(`¿Eliminar el día ${formatFecha(day.fecha)} del itinerario? El técnico no registró asistencia ese día.`)) return;
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${day.id}`);
      setDrawerReserva((prev) => ({
        ...prev,
        itinerario: prev.itinerario.filter((it) => it.id !== day.id),
      }));
    } catch (error) {
      console.error("Error al eliminar día del itinerario:", error);
      alert("No se pudo eliminar el día. Intenta de nuevo.");
    }
  };

  /* -------------------- Cancelar Reserva -------------------- */
  const handleCancelarReserva = async (tecnico) => {
    if (!tecnico.reserva?.id) {
      alert("No se puede cancelar: la reserva no tiene un ID válido.");
      return;
    }
    if (!window.confirm("¿Estás seguro de que deseas cancelar esta reserva?")) return;

    setCancelLoading(true);
    try {
      const res = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${tecnico.reserva.id}`);
      const fullReserva = res.data;

      const tieneReporteAsociado = (day) => {
        if (!day) return false;
        if (day.id_reporte && day.id_reporte !== null) return true;
        if (!fullReserva.reportes_servicio || fullReserva.reportes_servicio.length === 0) return false;
        return fullReserva.reportes_servicio.some((r) => r.itinerario_id === day.id);
      };

      const diasConReporte = fullReserva.itinerario.filter((day) => tieneReporteAsociado(day));
      const diasSinReporte = fullReserva.itinerario.filter((day) => !tieneReporteAsociado(day));

      if (diasConReporte.length > 0) {
        const confirmar = window.confirm(
          `Se eliminarán ${diasSinReporte.length} día(s) del itinerario. ${diasConReporte.length} día(s) con reporte asociado se conservarán. ¿Deseas continuar?`
        );
        if (!confirmar) { setCancelLoading(false); return; }
      }

      for (const day of diasSinReporte) {
        await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${day.id}`);
      }

      const updatedData = {
        ticketId: fullReserva.ticketid, empresa: fullReserva.empresa, contacto: fullReserva.contacto,
        equipo: fullReserva.equipo, numero_serie: fullReserva.numero_serie || "",
        ticket: fullReserva.ticket || "", fecha_reserva: fullReserva.fecha_reserva || "",
        hora_reserva: fullReserva.hora_reserva || "", status: "Cancelado",
        correo_cliente: fullReserva.correo_cliente || "", correo_tecnico: fullReserva.correo_tecnico || "",
        domicilio: fullReserva.domicilio, localidad: fullReserva.localidad,
        ciudad: fullReserva.ciudad, telefono: fullReserva.telefono,
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/reservas/${tecnico.reserva.id}`, updatedData, {
        headers: { "Content-Type": "application/json" },
      });

      alert("Reserva cancelada correctamente.");
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error("Error al cancelar la reserva:", error);
      alert("Error al cancelar la reserva.");
    } finally {
      setCancelLoading(false);
    }
  };

  /* ----------------------- Fetch ---------------------- */
  const fetchData = useCallback(async () => {
    try {
      const [allTec, checadas] = await Promise.all([
        axios.get("https://desarrollotecnologicoar.com/api5/tecnicos"),
        axios.get("https://desarrollotecnologicoar.com/api5/checadas_diarias"),
      ]);
      const checadasHoy = checadas.data;

      const tecnicoReservas = await Promise.all(
        allTec.data.map(async (t) => {
          try {
            const response = await axios.get(`https://desarrollotecnologicoar.com/api5/dia_actual_reservado/${t.correo_tecnico}`);
            const reservas = Array.isArray(response.data) ? response.data : [];
            return { ...t, reservas };
          } catch {
            return { ...t, reservas: [] };
          }
        })
      );

      const todos = tecnicoReservas.flatMap((t) => {
        if (t.reservas.length === 0) {
          const match = checadasHoy.find((c) => c.correo_tecnico === t.correo_tecnico);
          return [{ ...t, reserva: null, checado: !!match, hora_llegada: match ? match.hora_llegada : null }];
        }
        return t.reservas.map((r) => {
          const match = checadasHoy.find(
            (c) => c.correo_tecnico === t.correo_tecnico && c.ticket?.toString() === r.ticket?.toString()
          );
          return { ...t, reserva: r, checado: !!match, hora_llegada: match ? match.hora_llegada : null };
        });
      });

      const colorOrder = { red: 0, orange: 1, purple: 2, green: 3, gray: 4 };
      todos.sort((a, b) => colorOrder[getColor(a)] - colorOrder[getColor(b)]);

      setTecnicos(todos);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error("Error cargando datos", error);
      setLoading(false);
    }
  }, []);

  const filteredTecnicos = tecnicos.filter((tecnico) => {
    const matchesSede = filter.sede ? tecnico.sucursal?.toLowerCase() === filter.sede.toLowerCase() : true;
    const matchesStatus = filter.status
      ? filter.status === "checados" ? tecnico.checado : filter.status === "noChecados" ? !tecnico.checado : true
      : true;
    const matchesTecnico = filter.tecnicos.length > 0 ? filter.tecnicos.includes(tecnico.nombre_tecnico) : true;
    return matchesSede && matchesStatus && matchesTecnico;
  });

  const conteoFiltrado = {
    checados: filteredTecnicos.filter((t) => t.checado).length,
    noChecados: filteredTecnicos.filter((t) => !t.checado).length,
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatUpdateTime = (date) => {
    if (!date) return "";
    const h = date.getHours();
    const m = date.getMinutes();
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${m.toString().padStart(2, "0")} ${ampm}`;
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-2 border-gray-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );

  /* ------------------------------------------------------------------ */
  /*  Technician name filter (multi-select via checkboxes)              */
  /* ------------------------------------------------------------------ */
  const handleTecnicoToggle = (nombre) => {
    setFilter((prev) => ({
      ...prev,
      tecnicos: prev.tecnicos.includes(nombre)
        ? prev.tecnicos.filter((n) => n !== nombre)
        : [...prev.tecnicos, nombre],
    }));
  };

  const uniqueTecNames = [...new Set(tecnicos.map((t) => t.nombre_tecnico))];

  /* ================================================================== */
  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="page-title mb-0">
          Checadas del día{" "}
          <span className="text-sm font-normal text-gray-500">(actualizado {formatUpdateTime(lastUpdate)})</span>
        </h1>

        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setToggleActivo(false)}
            className={`px-3 py-2 flex items-center gap-1 text-sm font-medium transition-colors ${
              !toggleActivo ? "bg-slate-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <HiSquares2X2 className="h-5 w-5" />
          </button>
          <button
            onClick={() => setToggleActivo(true)}
            className={`px-3 py-2 flex items-center gap-1 text-sm font-medium transition-colors ${
              toggleActivo ? "bg-slate-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <HiListBullet className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Pie chart + filters */}
      <div className="card p-4 sm:p-5 mb-8 overflow-visible">
        <div className="flex flex-col xl:flex-row items-start gap-6 xl:gap-8">
          <div className="flex-shrink-0 w-full xl:w-auto overflow-visible">
            <PieChart width={380} height={250}>
            <Pie
              data={[
                { name: "En campo", value: conteoFiltrado.checados, fill: "#10b981" },
                { name: "Fuera de campo", value: conteoFiltrado.noChecados, fill: "#ef4444" },
              ]}
              dataKey="value"
              nameKey="name"
              cx={180}
              cy={115}
              outerRadius={80}
              innerRadius={40}
              label={({ name, value }) => `${name}: ${value.toFixed(0)}`}
              labelLine={false}
              stroke="#fff"
              strokeWidth={2}
              fillOpacity={0.8}
            />
            </PieChart>
          </div>

          <div className="flex flex-wrap gap-3 w-full">
            <div>
              <label className="label">Sede</label>
              <select
                className="input w-52"
                value={filter.sede}
                onChange={(e) => setFilter({ ...filter, sede: e.target.value })}
              >
                <option value="">Todas</option>
                <option value="Españoles">Españoles</option>
                <option value="Luis Molina">Luis Molina</option>
                <option value="CDMX">Ciudad de México</option>
                <option value="Monterrey">Monterrey</option>
                <option value="Guanajuato">Guanajuato</option>
                <option value="Ocotlán">Ocotlán</option>
              </select>
            </div>

            <div>
              <label className="label">Status</label>
              <select
                className="input w-52"
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="checados">Checados</option>
                <option value="noChecados">No checados</option>
              </select>
            </div>

            <div className="w-full sm:w-80">
              <div className="flex items-center justify-between mb-1.5">
                <label className="label mb-0">Técnicos</label>
                <span className="text-xs font-medium text-slate-600">
                  {filter.tecnicos.length} seleccionados
                </span>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-2.5 max-h-40 overflow-y-auto space-y-1 shadow-sm">
                {uniqueTecNames.map((nombre) => {
                  const selected = filter.tecnicos.includes(nombre);
                  return (
                    <label
                      key={nombre}
                      className={`flex items-center gap-2.5 text-sm cursor-pointer rounded-md px-2 py-1.5 transition-colors ${
                        selected ? "bg-slate-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => handleTecnicoToggle(nombre)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-900 focus:ring-slate-500"
                      />
                      <span className="truncate text-gray-700">{nombre}</span>
                    </label>
                  );
                })}
              </div>
              {filter.tecnicos.length > 0 && (
                <button
                  className="text-xs font-medium text-blue-600 hover:underline mt-2"
                  onClick={() => setFilter({ ...filter, tecnicos: [] })}
                >
                  Limpiar selección
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ========================== Cards view ========================== */}
      {!toggleActivo ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTecnicos.map((tecnico) => {
            const color = getColor(tecnico);
            const delayMinutes = tecnico.reserva?.entrada
              ? Math.abs(tiempoRetraso(tecnico.hora_llegada, tecnico.reserva.entrada))
              : null;

            return (
              <div
                key={`${tecnico.correo_tecnico}-${tecnico.reserva?.ticket || "sinreserva"}`}
                onClick={() => handleOpenModal(tecnico)}
                className={`card-hover p-4 sm:p-5 border-l-4 ${borderColorMap[color]} cursor-pointer transition-transform hover:scale-[1.02]`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-700 shrink-0">
                    {tecnico.nombre_tecnico?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{tecnico.nombre_tecnico}</p>
                    <p className="text-xs text-gray-500">{tecnico.puesto || "---"} | {tecnico.sucursal}</p>
                    {tecnico.reserva && (
                      <div className="mt-2 space-y-0.5 text-sm text-gray-600">
                        <p><strong>Ticket:</strong> {tecnico.reserva.ticket}</p>
                        <p><strong>Hora acordada:</strong> {formatHora(tecnico.reserva.entrada)} -- {tecnico.reserva.tipo_servicio}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                      tecnico.checado
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-gray-200 bg-gray-50 text-gray-600"
                    }`}
                  >
                    {tecnico.checado ? (
                      <HiCheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <HiXCircle className="h-4 w-4 text-gray-400" />
                    )}
                    {tecnico.checado
                      ? `Marcó entrada: ${formatHora(tecnico.hora_llegada)}${delayMinutes !== null ? ` (${delayMinutes} min)` : ""}`
                      : "Sin checada"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ========================== List view ========================== */
        <div className="space-y-3">
          {filteredTecnicos.map((tecnico) => {
            const color = getColor(tecnico);
            return (
              <div
                key={`${tecnico.correo_tecnico}-${tecnico.reserva?.ticket || "sinreserva"}`}
                onClick={() => handleOpenModal(tecnico)}
                className={`card-hover p-4 border-l-4 ${borderColorMap[color]} flex items-center gap-4 cursor-pointer`}
              >
                <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-700 shrink-0">
                  {tecnico.nombre_tecnico?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{tecnico.nombre_tecnico}</p>
                  <p className="text-xs text-gray-500">{tecnico.puesto || "---"} | {tecnico.sucursal}</p>
                  {tecnico.reserva && (
                    <div className="mt-1 text-sm text-gray-600">
                      <strong>Ticket:</strong> {tecnico.reserva.ticket} &middot;{" "}
                      <strong>Hora:</strong> {formatHora(tecnico.reserva.entrada)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========================== Drawer ========================== */}
      {openModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={handleCloseModal} />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-700">
                  {selectedTecnico.nombre_tecnico?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedTecnico.nombre_tecnico}</h2>
                  <p className="text-xs text-gray-500">{selectedTecnico.puesto || "---"} | {selectedTecnico.sucursal}</p>
                </div>
              </div>
              <button className="btn-icon" onClick={handleCloseModal}>
                <HiXMark className="h-5 w-5" />
              </button>
            </div>

            {/* Cancel Reservation Button */}
            {selectedTecnico.reserva && (
              <div className="px-5 py-3 border-b border-gray-200 shrink-0">
                <button
                  onClick={() => handleCancelarReserva(selectedTecnico)}
                  disabled={cancelLoading}
                  className="w-full py-2.5 px-4 inline-flex items-center justify-center gap-2 bg-red-600 text-white rounded-btn font-semibold text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <HiOutlineXCircle className="w-4 h-4" />
                  {cancelLoading ? "Cancelando..." : "Cancelar Reserva"}
                </button>
                <button
                  onClick={() => navigate(`/editar_solicitud/${selectedTecnico.reserva.id}/aprobados`)}
                  className="w-full mt-2 py-2.5 px-4 inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-btn font-semibold text-sm hover:bg-slate-800 transition-colors"
                >
                  <HiOutlinePencilSquare className="w-4 h-4" />
                  Editar Reserva
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {/* Check-in Status */}
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg mb-5 ${
                selectedTecnico.checado
                  ? "bg-emerald-50 border border-emerald-200"
                  : "bg-gray-50 border border-gray-200"
              }`}>
                {selectedTecnico.checado ? (
                  <HiCheckCircle className="h-5 w-5 text-emerald-500 shrink-0" />
                ) : (
                  <HiXCircle className="h-5 w-5 text-gray-400 shrink-0" />
                )}
                <span className={`text-sm font-medium ${selectedTecnico.checado ? "text-emerald-700" : "text-gray-600"}`}>
                  {selectedTecnico.checado ? `Marcó entrada: ${formatHora(selectedTecnico.hora_llegada)}` : "Sin checada"}
                </span>
              </div>

              {drawerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-7 w-7 border-2 border-gray-300 border-t-slate-900 rounded-full animate-spin" />
                </div>
              ) : selectedTecnico.reserva ? (
                <div>
                  {/* Empresa title */}
                  <h2 className="text-xl font-bold text-slate-900 mb-5">
                    {(drawerReserva || selectedTecnico.reserva).empresa}
                  </h2>

                  {/* Detail rows */}
                  <div className="space-y-0">
                    {[
                      { label: "Cliente", value: (drawerReserva || selectedTecnico.reserva).contacto || (drawerReserva || selectedTecnico.reserva).empresa },
                      { label: "Maquina", value: (drawerReserva || selectedTecnico.reserva).equipo || "---" },
                      { label: "Solicitado por", value: drawerReserva?.nombre_agente || "---" },
                      { label: "Tecnico", value: selectedTecnico.nombre_tecnico || "No asignado" },
                      { label: "Localidad", value: drawerReserva?.localidad || "---" },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between py-2.5 border-b border-slate-100">
                        <span className="text-sm text-slate-500">{item.label}</span>
                        <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">{item.value}</span>
                      </div>
                    ))}

                    {/* Ubicación */}
                    <div className="flex items-start gap-2 py-2.5 border-b border-slate-100">
                      <HiOutlineMapPin className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-500 mb-0.5">Ubicacion</p>
                        <a
                          href={`https://www.google.com/maps/search/${encodeURIComponent((drawerReserva || selectedTecnico.reserva).domicilio || "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-slate-800 hover:text-blue-600 hover:underline"
                        >
                          {(drawerReserva || selectedTecnico.reserva).domicilio || "---"}
                        </a>
                      </div>
                    </div>

                    {/* Estatus */}
                    <div className="flex items-center gap-2 py-2.5 border-b border-slate-100">
                      <HiOutlineCheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-500 mb-0.5">Estatus</p>
                        <span className={statusBadge((drawerReserva || selectedTecnico.reserva).status || "---")}>
                          {(drawerReserva || selectedTecnico.reserva).status || "---"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actividades */}
                  <div className="mt-6">
                    <h3 className="section-title mb-3">Actividades</h3>
                    {drawerReserva?.actividades?.length > 0 ? (
                      <div className="space-y-2">
                        {drawerReserva.actividades.map((act, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                            <HiOutlineChatBubbleLeft className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="capitalize">{typeof act === "object" ? act.nombre_actividad : act}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No hay actividades registradas</p>
                    )}
                  </div>

                  {/* Itinerario */}
                  <div className="mt-6">
                    <h3 className="section-title mb-3">Itinerario</h3>
                    {drawerReserva?.itinerario?.length > 0 ? (
                      <div className="space-y-2">
                        {drawerReserva.itinerario.map((it, i) => {
                          const fechaDia = new Date(it.fecha.split("T")[0] + "T00:00:00");
                          const hoy = new Date(); hoy.setHours(0,0,0,0);
                          const esPasado = fechaDia < hoy;
                          const tieneReporte = drawerReserva.reportes_servicio?.some((r) => r.itinerario_id === it.id);
                          const puedeEliminar = esPasado && !tieneReporte;
                          return (
                            <div key={i} className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${puedeEliminar ? "bg-red-50 border border-red-100" : "bg-slate-50"}`}>
                              <div>
                                <span className="font-medium text-slate-800">{formatFecha(it.fecha)}</span>
                                {puedeEliminar && (
                                  <span className="ml-2 text-xs text-red-400 font-medium">Sin asistencia</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500">{formatHora(it.entrada)} -- {formatHora(it.salida)}</span>
                                {puedeEliminar && (
                                  <button
                                    title="Eliminar día sin asistencia"
                                    className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
                                    onClick={() => handleBorrarDiaItinerario(it)}
                                  >
                                    <HiTrash className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No hay itinerario registrado</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <HiXCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">{selectedTecnico.nombre_tecnico} no tiene reserva asignada hoy.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OverviewChecadas;
